import { assign, omit } from 'lodash';
import React, { useState } from 'react';

import { formatCoverageDate } from '../../../../utils/coverageDateFormat';
import { formatCADCentsOptional } from '../../../../utils/currencyFormat';
import { limitsLabels } from './CoveragesDetails';

interface AdditionalCoveragesProps {
  limits: Record<string, number>;
  carrierPartner: string;
  /** Coverage this dropdown belongs to — 'cgl' or 'eo'. */
  name: string;
  /**
   * E&O only: the claims-made retroactive date as YYYY-MM-DD. Absent on older
   * quotes rated before the backend started returning it, in which case the
   * row is simply not shown.
   */
  retroactiveDate?: string | null;
}

export const AdditionalCoverages: React.FC<AdditionalCoveragesProps> = ({
  carrierPartner,
  limits,
  name,
  retroactiveDate
}) => {
  // TODO: Move some of these hardcoded CGL limit information to the backend
  const additionalCGLLimitsStart = {
    productAggregateLimit: limits['aggregateLimit'],
    personalAdvertisingInjury: limits['occurrenceLimit']
  };

  const additionalCGLLimitsEnd = {
    productAggregateLimit: limits['aggregateLimit'],
    personalAdvertisingInjury: limits['occurrenceLimit'],
    blanketAdditionalInsured: 'INCLUDED',
    blanketWaiverOfSubrogation: 'INCLUDED',
    ...(carrierPartner === 'StateNational' && {
      primaryAndNonContributory: 'INCLUDED'
    })
  };

  const duplicateLimits = ['aggregateLimit', 'occurrenceLimit', 'deductible'];

  // E&O carries the same two blanket endorsements as GL, priced into the premium
  // rather than charged separately. It has no products/personal-injury
  // equivalents, and its limits are all already listed on the card above, so the
  // dropdown holds only the endorsements.
  // The retroactive date is the third row, after the endorsements: it is the
  // date coverage reaches back to, not a limit, so the backend sends it
  // alongside the limits rather than inside them.
  const formattedRetroactiveDate = retroactiveDate
    ? formatCoverageDate(retroactiveDate)
    : undefined;

  const additionalEOLimits = {
    blanketAdditionalInsured: 'INCLUDED',
    blanketWaiverOfSubrogation: 'INCLUDED',
    ...(formattedRetroactiveDate && {
      retroactiveDate: formattedRetroactiveDate
    })
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filteredAdditionalLimitsObject: Record<string, any> =
    name === 'eo'
      ? additionalEOLimits
      : assign(
          additionalCGLLimitsStart,
          assign(omit(limits, duplicateLimits), additionalCGLLimitsEnd)
        );

  const [displayAdditionalCoverage, setDisplayAdditionalCoverage] = useState(
    false
  );
  return (
    <div>
      <div
        className="inline text-secondary font-semibold cursor-pointer border-b border-secondary"
        onClick={() => setDisplayAdditionalCoverage(!displayAdditionalCoverage)}
      >
        See More Coverage Information{' '}
        {displayAdditionalCoverage ? (
          <svg
            className="fill-secondary inline h-5 mb-1 MuiSelect-iconOpen"
            focusable="false"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M7 10l5 5 5-5z"></path>
          </svg>
        ) : (
          <svg
            className="fill-secondary inline h-5 mb-1"
            focusable="false"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M7 10l5 5 5-5z"></path>
          </svg>
        )}
      </div>
      {displayAdditionalCoverage
        ? Object.keys(filteredAdditionalLimitsObject).map((limit, index) => (
            <div
              key={index}
              className="mt-1 w-full grid grid-cols-2 md:grid-cols-3"
            >
              <div className="font-medium">
                {/* Limits are numbers and render as currency; the endorsement
                    markers and the retroactive date are already display
                    strings. */}
                {typeof filteredAdditionalLimitsObject[limit] === 'number'
                  ? formatCADCentsOptional.format(
                      filteredAdditionalLimitsObject[limit]
                    )
                  : filteredAdditionalLimitsObject[limit]}
              </div>
              <div className="font-semibold md:col-span-2">
                {limitsLabels[limit]}
              </div>
            </div>
          ))
        : null}
    </div>
  );
};
